import { useMemo, useState } from "react";
import {
  defaultDriveFolderRecipe,
  parseDriveFolderRecipe,
  parseDriveFolderRecipeCompact,
  recipeFromUnknown,
  serializeDriveFolderRecipe,
  type DriveFolderRecipe,
} from "@/lib/ops/driveFolderRecipe";
import { DRIVE_RECIPE_PREVIEW_FACTS } from "@/lib/ops/driveDealFolderFacts";
import { previewDriveFolderRecipe } from "@/lib/ops/driveFolderRecipePreview";
import { OpsDriveFolderRecipeChart } from "./OpsDriveFolderRecipeChart";

export function OpsDriveFolderRecipeEditor(props: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [view, setView] = useState<"chart" | "compact" | "json">("chart");
  const [previewId, setPreviewId] = useState(DRIVE_RECIPE_PREVIEW_FACTS[1]!.id);
  const [compactDraft, setCompactDraft] = useState(() =>
    serializeDriveFolderRecipe(parseDriveFolderRecipe(props.value), "compact")
  );
  const [jsonError, setJsonError] = useState("");

  const [jsonDraft, setJsonDraft] = useState(() => serializeDriveFolderRecipe(parseDriveFolderRecipe(props.value), "json"));

  const recipe = useMemo(() => parseDriveFolderRecipe(props.value), [props.value]);
  const previewFacts = DRIVE_RECIPE_PREVIEW_FACTS.find((f) => f.id === previewId) ?? DRIVE_RECIPE_PREVIEW_FACTS[0]!;

  function setRecipe(next: DriveFolderRecipe) {
    setJsonError("");
    const stored = serializeDriveFolderRecipe(next, view === "compact" ? "compact" : "json");
    props.onChange(stored);
    setCompactDraft(serializeDriveFolderRecipe(next, "compact"));
    setJsonDraft(serializeDriveFolderRecipe(next, "json"));
  }

  function applyCompact(text: string) {
    setCompactDraft(text);
    const parsed = parseDriveFolderRecipeCompact(text.trim()) ?? parseDriveFolderRecipe(text);
    if (!parsed.nodes.length && text.trim()) {
      setJsonError("Could not parse that folder recipe.");
      return;
    }
    setJsonError("");
    props.onChange(text.trim());
  }

  function applyJson(text: string) {
    setJsonDraft(text);
    try {
      const parsed = JSON.parse(text) as unknown;
      const recipeNext = recipeFromUnknown(parsed);
      if (!recipeNext) {
        setJsonError("JSON must be a version 1 recipe with a nodes array.");
        return;
      }
      setJsonError("");
      props.onChange(serializeDriveFolderRecipe(recipeNext, "json"));
      setCompactDraft(serializeDriveFolderRecipe(recipeNext, "compact"));
    } catch {
      setJsonError("JSON is not valid yet.");
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-medium text-gray-700">Folder recipe</p>
        <div className="flex rounded border border-gray-300 text-[11px]">
          {(["chart", "compact", "json"] as const).map((id) => (
            <button
              key={id}
              type="button"
              className={`px-2 py-0.5 ${view === id ? "bg-indigo-50 font-semibold" : ""}`}
              onClick={() => setView(id)}
            >
              {id === "chart" ? "Chart" : id === "compact" ? "Compact" : "JSON"}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="text-[11px] text-indigo-800 underline"
          onClick={() => setRecipe(defaultDriveFolderRecipe())}
        >
          Reset to Pending / Cancelled / Delivered
        </button>
      </div>
      <p className="text-[11px] text-gray-500 leading-snug">
        Cards at the same level with a “When” rule are alternatives (one folder is picked from the deal). Cards set to
        Always are created after that choice — so Delivered → RTO → year → month. Drag a card onto another to nest it,
        or onto the gap between cards to reorder. Compact form:{" "}
        <code className="text-[10px]">{"{Pending,Cancelled/{RTO Rejected,Customer Cancelled}/{year}}"}</code>
      </p>
      {view === "chart" ? (
        <OpsDriveFolderRecipeChart nodes={recipe.nodes} onChange={(nodes) => setRecipe({ version: 1, nodes })} />
      ) : null}
      {view === "compact" ? (
        <textarea
          className="w-full min-h-[5rem] rounded border border-gray-300 px-2 py-1 font-mono text-[11px]"
          value={compactDraft}
          onChange={(e) => applyCompact(e.target.value)}
        />
      ) : null}
      {view === "json" ? (
        <textarea
          className="w-full min-h-[8rem] rounded border border-gray-300 px-2 py-1 font-mono text-[11px]"
          value={jsonDraft}
          onChange={(e) => applyJson(e.target.value)}
        />
      ) : null}
      {jsonError ? <p className="text-[11px] text-red-700">{jsonError}</p> : null}
      <label className="block text-[11px] text-gray-600">
        Preview path
        <select
          className="ml-2 rounded border border-gray-300 px-1 py-0.5"
          value={previewId}
          onChange={(e) => setPreviewId(e.target.value)}
        >
          {DRIVE_RECIPE_PREVIEW_FACTS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="mt-1 block font-mono text-[11px] text-gray-800">{previewDriveFolderRecipe(recipe, previewFacts.facts)}</span>
      </label>
    </div>
  );
}
